import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Calendar, User, Facebook, Linkedin, Mail as MailIcon, Link2, Check } from "lucide-react";
import SEO from "@/components/SEO";
import { toast } from "sonner";

export function BlogList() {
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/blog");
        setPosts(Array.isArray(data) ? data : []);
      } catch (_) { setPosts([]); }
    })();
  }, []);
  return (
    <div className="pt-36 pb-24" data-testid="blog-list">
      <SEO
        title="Aviana Journal — Essays on Aerial Yoga, Teaching & the Inner Work"
        description="Long-form essays by Holly Johnson on aerial yoga, the business of teaching, restorative practice, retreat curation, and the sacred-feminine inner work that makes embodied teaching possible."
        path="/journal"
        keywords="aerial yoga blog, aerial yoga essays, yoga teacher journal, Holly Johnson writing, aerial yoga income, women's yoga writing"
      />
      <div className="container-aviana">
        <p className="overline mb-5">Journal</p>
        <h1 className="font-display text-6xl md:text-7xl text-aviana-ink max-w-4xl leading-[0.98]">
          Essays from the <em className="italic font-serif gold-text">silks.</em>
        </h1>
        <p className="mt-6 max-w-xl text-aviana-muted leading-[1.85]">
          A small collection of writing on aerial yoga, the business of teaching, and the inner work that
          makes both possible. New essays now published on Substack.
        </p>
        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-10">
          {posts.map((p) => (
            <Link to={`/journal/${p.slug}`} key={p.id} className="group" data-testid={`blog-card-${p.slug}`}>
              {p.image_url ? (
                <div className="img-zoom rounded-[2rem] aspect-[4/3] mb-6 ring-1 ring-aviana-border">
                  <img src={p.image_url.startsWith("http") ? p.image_url : `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "")}${p.image_url}`} alt={p.title} />
                </div>
              ) : (
                <div className="rounded-[2rem] aspect-[4/3] mb-6 ring-1 ring-aviana-border bg-gradient-to-br from-aviana-plumDeep via-aviana-plum to-aviana-plumDeep flex items-center justify-center">
                  <span className="text-[10px] tracking-[0.3em] uppercase text-aviana-goldSoft/70">Aviana Journal</span>
                </div>
              )}
              <p className="overline mb-3">{new Date(p.published_at).toLocaleDateString("en-US", { month: "long", year: "numeric" })}</p>
              <h2 className="font-display text-3xl text-aviana-ink leading-[1.1] group-hover:text-aviana-plum transition-colors">{p.title}</h2>
              <p className="mt-3 text-sm text-aviana-muted leading-[1.75] line-clamp-3">{p.excerpt}</p>
            </Link>
          ))}
        </div>
        {posts.length === 0 && (
          <p className="mt-16 text-aviana-muted">More essays coming soon. Follow the journey on <a href="https://substack.com" className="text-aviana-plum underline">Substack →</a></p>
        )}
      </div>
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [authorImg, setAuthorImg] = useState("");
  const [authorBio, setAuthorBio] = useState("");
  useEffect(() => { (async () => {
    try {
      const { data } = await api.get(`/blog/${slug}`);
      setPost(data);
    } catch { setPost(false); }
  })(); }, [slug]);

  // Author card wiring — the headshot is curated at Admin → Page Images →
  // Journal → author-portrait; the bio lives on site settings so Holly can
  // rewrite it from the Settings tab without touching code.
  useEffect(() => {
    (async () => {
      try {
        const [pi, s] = await Promise.all([
          api.get("/page-images/journal"),
          api.get("/settings"),
        ]);
        const urls = pi.data?.slots?.["author-portrait"] || [];
        if (urls[0]) setAuthorImg(urls[0]);
        if (s.data?.author_bio) setAuthorBio(s.data.author_bio);
      } catch { /* card falls back to defaults */ }
    })();
  }, []);

  if (post === null) return <div className="pt-40 container-aviana text-aviana-muted">Loading…</div>;
  if (post === false) return <div className="pt-40 container-aviana text-aviana-muted">Essay not found.</div>;

  return (
    <article className="pt-32 pb-24" data-testid="blog-post">
      <SEO
        title={post.title}
        description={post.excerpt || `${post.title} — an essay from the Aviana Journal by Holly Johnson.`}
        path={`/journal/${post.slug}`}
        image={post.image_url || "/social/og-restorative-ytt.jpg"}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": post.title,
          "image": post.image_url,
          "datePublished": post.published_at,
          "author": { "@type": "Person", "name": post.author || "Holly Johnson", "@id": "https://avianayoga.com/#holly" },
          "publisher": { "@id": "https://avianayoga.com/#organization" },
          "mainEntityOfPage": `https://avianayoga.com/journal/${post.slug}`,
        }}
      />
      <div className="container-aviana max-w-3xl">
        <Link to="/journal" className="inline-flex items-center gap-2 text-sm text-aviana-muted hover:text-aviana-ink mb-10">
          <ArrowLeft size={14} /> All essays
        </Link>
        <p className="overline mb-5">Journal</p>
        <h1 className="font-display text-5xl md:text-6xl text-aviana-ink leading-[1.02]">{post.title}</h1>
        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-aviana-muted">
          <span className="inline-flex items-center gap-2"><Calendar size={14} className="text-aviana-gold" />{new Date(post.published_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
          <span className="inline-flex items-center gap-2"><User size={14} className="text-aviana-gold" />{post.author}</span>
        </div>
        {post.image_url && (
          <div className="img-zoom rounded-[2rem] aspect-[16/9] my-12 ring-1 ring-aviana-border">
            <img src={post.image_url} alt={post.title} />
          </div>
        )}
        <div className="prose prose-lg max-w-none mt-10 text-aviana-inkSoft leading-[1.85] aviana-richtext journal-body"
             dangerouslySetInnerHTML={{ __html: post.content_html }} />

        <ShareRow title={post.title} slug={post.slug} />
        <AuthorCard image={authorImg} bio={authorBio} name={post.author || "Holly Johnson"} />
      </div>
    </article>
  );
}

// Share row — X, Facebook, LinkedIn, Email, and "copy link". Uses the live
// document.location so the URL is always the canonical post URL, whether the
// user is on preview, production, or a local dev server.
function ShareRow({ title, slug }) {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined"
    ? `${window.location.origin}/journal/${slug}`
    : `https://avianayoga.com/journal/${slug}`;
  const shareText = `${title} — Aviana Journal`;

  const links = [
    {
      key: "x",
      label: "Share on X",
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`,
      // lucide-react no longer ships a Twitter/X icon; use inline SVG mark
      icon: (
        <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      ),
    },
    { key: "facebook", label: "Share on Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, icon: <Facebook size={15} /> },
    { key: "linkedin", label: "Share on LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, icon: <Linkedin size={15} /> },
    { key: "email", label: "Share via email", href: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`I thought you'd love this essay from the Aviana Journal:\n\n${url}`)}`, icon: <MailIcon size={15} /> },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy link — try again");
    }
  };

  return (
    <div className="mt-16 pt-8 border-t border-aviana-border" data-testid="share-row">
      <p className="overline-plum mb-4">Share this essay</p>
      <div className="flex flex-wrap items-center gap-3">
        {links.map((l) => (
          <a
            key={l.key}
            href={l.href}
            target={l.key === "email" ? undefined : "_blank"}
            rel="noopener noreferrer"
            aria-label={l.label}
            title={l.label}
            className="w-11 h-11 rounded-full border border-aviana-border text-aviana-inkSoft hover:text-aviana-plum hover:border-aviana-plum bg-aviana-cream/60 hover:bg-aviana-cream flex items-center justify-center transition-all"
            data-testid={`share-${l.key}`}
          >
            {l.icon}
          </a>
        ))}
        <button
          onClick={copy}
          aria-label={copied ? "Link copied" : "Copy link"}
          title={copied ? "Copied" : "Copy link"}
          className={`w-11 h-11 rounded-full border flex items-center justify-center transition-all ${copied ? "border-aviana-plum bg-aviana-plum/10 text-aviana-plum" : "border-aviana-border text-aviana-inkSoft hover:text-aviana-plum hover:border-aviana-plum bg-aviana-cream/60 hover:bg-aviana-cream"}`}
          data-testid="share-copy"
        >
          {copied ? <Check size={15} /> : <Link2 size={15} />}
        </button>
      </div>
    </div>
  );
}

// About-the-author card — Holly's headshot in a circle next to her bio.
// Falls back to initials-in-a-plum-circle so the layout stays elegant even
// before an image has been curated.
function AuthorCard({ image, bio, name }) {
  const initials = name.split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="mt-12 rounded-[2rem] bg-aviana-cream border border-aviana-border p-6 sm:p-8 flex items-start gap-6 flex-wrap sm:flex-nowrap" data-testid="author-card">
      <div className="flex-shrink-0">
        {image ? (
          <img
            src={image}
            alt={name}
            className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover ring-2 ring-aviana-gold/70 ring-offset-2 ring-offset-aviana-cream"
            data-testid="author-headshot"
          />
        ) : (
          <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-aviana-plum/15 text-aviana-plum flex items-center justify-center font-display text-3xl ring-2 ring-aviana-gold/70 ring-offset-2 ring-offset-aviana-cream" data-testid="author-headshot-fallback">
            {initials}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="overline-plum mb-2">Written by</p>
        <p className="font-display text-2xl text-aviana-ink leading-tight">{name}</p>
        <p className="mt-3 text-[14.5px] text-aviana-inkSoft leading-[1.75] whitespace-pre-line">
          {bio || "Holly Johnson is the founder of Aviana Yoga — a teacher trainer, healer, and retreat leader guiding women home to themselves through movement, breath, and the sacred feminine."}
        </p>
        <Link to="/about" className="mt-4 inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-aviana-plum hover:text-aviana-gold" data-testid="author-more-link">
          Meet Holly →
        </Link>
      </div>
    </div>
  );
}
